import merge from 'lodash/merge';
import isEqual from 'lodash/isEqual';
import {v4} from 'uuid';

import Manager from 'coreClasses/Manager';
import {ABTestsResponse, CustomerGroup, ICustomerStateStore} from 'coreStore/data/customer/types';
import Customer from './Customer';
import {
	resetToInitialState,
	receiveContext,
	receiveCustomer,
	receiveCustomerGroups,
	receiveNewsletterSubscribe,
	receiveNewsletterCustomization,
	selectContext,
	selectCustomer,
	receiveABTestParticipations,
} from 'coreStore/data/customer';
import {resetBasket} from 'coreStore/data/basket';
import {resetCustomerProductLists} from 'coreStore/data/customerProductLists';
import {nanoid} from 'nanoid';
import ManagerRepository from '../ManagerRepository';
import {
	ICustomerAddress,
	ICustomerAPI,
	ICustomerCustomQualifiers,
	ICustomerLoginCredentials,
	ICustomerPaymentInstrument,
	ICustomerProfilePayload,
	ICustomerQualifiers,
	ICustomerRegistrationData,
	ICustomerRegistrationPayload,
	ICustomerUpdatePasswordData,
	INewsletterProfile,
	INewsletterPreferences,
	ECustomerAuthType,
	ECustomerGender,
	INewsletterSource
} from './ICustomer';
import {IIdentityProviderCredentials} from 'coreClasses/identity/IIdentityProvider';
import {formatDateToISOString, sortArrayByDate} from 'coreUtils/dates';
import {isServer, cleanObject, cloneObject, requestIdleCallback} from 'coreUtils/utils';
import {EEventSource, EEventType} from 'coreClasses/events/IEvents';
import PWAException from 'coreClasses/PWAException';
import EmailValidationError from 'coreClasses/EmailValidationError';
import EmailAlreadyInUseError from 'coreClasses/EmailAlreadyInUseError';
import CookiesMgr from 'coreClasses/cookies/CookiesMgr';
import Cookie from 'coreClasses/cookies/Cookie';
import {LogSource} from 'commerceAPI/custom-apis/CustomShopperSystem/ICustomShopperSystem';

import {
	IUpdateNewsletterProfileOptions,
	ISearchNewsletterProfileOptions,
	IUnsubscribeFromNewsletterByUidResponse
} from 'commerceAPI/custom-apis/CustomShopperNewsletter/ICustomShopperNewsletter';
import {resolveGenderFromSalutation} from './getGenderFromSalutation';
import Observer from 'coreClasses/patterns/observable/Observer';

const errorsMapping = {
	"The update password request is invalid. Customer's current password is not valid":
		'updatePassword.wrongCurrentPassword'
};

const REFRESH_CONTEXT_USER_DAYS_TIME = {
	REGISTERED: 7,
	GUEST: 1
};

const CONTEXT_ERROR_THRESHOLD = 2;

/**
 * Represents a customer manager
 */
export class CustomerMgr extends Manager {
	public CONTEXT_PLATFORM_WEB: string = 'web';
	private contextUpdatingPromises: Map<string, Promise<void>> = new Map();
	private contextValidatingPromise: Promise<void> | null;
	private contextErrorCounter = 0;
	private loginPromise: Promise<Customer>;

	/**
	 * @constructor
	 * @param {ManagerRepository} managerRepository - ManagerRepository instance
	 */
	constructor(managerRepository: ManagerRepository) {
		super(managerRepository);

		this.managerRepository.getErrorsStreamMgr().subscribe(this as unknown as Observer);
	}

	/**
	 * Receives Observer messages from ErrorsStreamMgr
	 * @param {string} _key - message key
	 * @param {object} _data - data
	 */
	update(_key: string, _data: object): void {
		return;
	}

	/**
	 * Returns the current customer instance.
	 * @returns {Customer} current customer
	 */
	getCurrentCustomer(): Customer {
		const customerFromStore: ICustomerStateStore = this.selectCustomerFromStore();

		return new Customer(customerFromStore, this.managerRepository);
	}

	/**
	 * Prepares data for new customers starting from the form.
	 * @param {ICustomerRegistrationData} data - registration data object
	 * @param {String} newsletterSource - newsletter subscription source
	 * @param {String} registrationDevice - device type used for registration
	 * @param {String} subscriptionSource - subscription source
	 * @returns {Customer} customer
	 */
	prepareRegistrationData(
		data: ICustomerRegistrationData,
		newsletterSource: string = null,
		registrationDevice: string = null,
		subscriptionSource: string = null,
	): Partial<ICustomerProfilePayload> & {login?: string; phoneHome?: string; birthday?: string} {
		const currentUrlParameters = new URL(window.location.href);
		const searchParams = new URLSearchParams(currentUrlParameters.search);
		const currentLocale = this.managerRepository.getLocalesMgr().getCurrentLocale();
		const gender = resolveGenderFromSalutation(data.gender, data.salutation);

		const customer = {
			firstName: data.firstName,
			lastName: data.lastName,
			email: data.email,
			login: data.email,
			phoneHome: data.phone || undefined,
			gender,
			salutation: data.salutation,
			c_privacyAuthorization: data.privacyAuthorization ?? true,
			c_newsletter: data.acceptsMarketing ?? true,
			c_joinToLoyalty: data.joinToLoyalty || false,
			c_authorizationDataCollection: data.authorizationDataCollection || false,
			c_languageCode: currentLocale.getLanguageCode(),
			c_visa_gold_chameleon: !!searchParams.get('visa_gold_chameleon'),
			c_authorizationOrderToken: searchParams.get('authorizationOrderToken'),
			c_orderNo: searchParams.get('orderNo'),
			c_phoneCode: data.phoneCode || undefined,
			c_source: newsletterSource || '',
			c_subscriptionByChannel: subscriptionSource || '',
			c_device: registrationDevice || '',
			c_newsletterSubscriptions: data?.newsletterSubscriptions || [],
			c_newsFor: data.newsFor || undefined,
			c_contactEmail: data.contactEmail ?? false,
			c_contactSms: data.contactSms ?? false,
			c_contactWhatsapp: data.contactWhatsapp ?? false,
			c_contactPhone: data.contactPhone ?? false,
			c_city: data.city,
			c_countryCode: data.country || currentLocale.getCountryCode(),
			c_citizenship: data.citizenship || undefined,
		} as unknown as Partial<ICustomerProfilePayload> & {login?: string; phoneHome?: string; birthday?: string};

		if (typeof data.USConsent === 'boolean') {
			customer.c_USConsent = data.USConsent;
		}

		const birthday: string | null = data.birthday ? formatDateToISOString(data.birthday) : null;

		if (birthday) {
			customer.birthday = birthday;
		}

		return customer;
	}

	/**
	 * Store customer to store
	 * @param {ICustomerAPI} customerAPI - customer
	 * @param {Boolean} isJustRegistered - set to true if customer comes from registration
	 * @param {Boolean} isAgent - true if is agent
	 * @param {Boolean} isSessionBridged - true if the session is already bridged
	 */
	private storeCustomer(
		customerAPI: ICustomerAPI,
		isJustRegistered: boolean = false,
		isAgent: boolean = false,
		isSessionBridged: boolean = false,
	): any {
		let storeCredits = null;

		if (customerAPI?.c_storeCredits) {
			try {
				storeCredits = JSON.parse(customerAPI?.c_storeCredits);
			} catch {} // eslint-disable-line no-empty
		}

		const oldCustomer = this.getCurrentCustomer();
		const currentCountry = this.managerRepository.getLocalesMgr().getCurrentCountry();
		const defaultSite = this.managerRepository.getLocalesMgr().getDefaultSite() as {id: string};
		const siteId = currentCountry?.getSiteId() || defaultSite.id;
		const customer = {
			authType: customerAPI.authType,
			customerId: customerAPI.customerId,
			customerNo: customerAPI.customerNo,
			addresses: customerAPI.addresses,
			email: customerAPI.email,
			firstName: customerAPI.firstName,
			lastName: customerAPI.lastName,
			birthday: customerAPI.birthday,
			phoneCode: customerAPI.c_phoneCode,
			phone: customerAPI.phoneHome,
			city: customerAPI.c_city,
			country: customerAPI.c_country,
			citizenship: customerAPI.c_citizenship,
			gender: customerAPI.gender,
			salutation: customerAPI.salutation ?? '',
			privacyAuthorization: customerAPI.c_privacyAuthorization,
			authorizationDataCollection: customerAPI.c_authorizationDataCollection,
			USConsent: customerAPI.c_USConsent,
			paymentInstruments:
				customerAPI?.paymentInstruments &&
				(sortArrayByDate(customerAPI.paymentInstruments, 'creationDate', false) as unknown as ICustomerPaymentInstrument[]),
			storeCredits: storeCredits,
			newsletter: customerAPI.c_newsletter,
			crmId: customerAPI.c_crmId,
			newsletterSubscriptions: customerAPI.c_newsletterSubscriptions,
			contactEmail: !!customerAPI.c_contactEmail,
			contactSms: !!customerAPI.c_contactSms,
			contactWhatsapp: !!customerAPI.c_contactWhatsapp,
			contactPhone: !!customerAPI.c_contactPhone,
			isLoginOnBehalf: customerAPI.c_canLoginOnBehalf,
			isJustRegistered,
			isServer: isServer,
			agent: isAgent,
			siteId: siteId,
			needsCommerceContext: customerAPI.needsCommerceContext,
			usid: (this.managerRepository.getCommerceApi() as any).auth.usid,
			needsBasketMerge:
				oldCustomer.authType === ECustomerAuthType.GUEST &&
				customerAPI.authType === ECustomerAuthType.REGISTERED &&
				oldCustomer.siteId === this.managerRepository.getLocalesMgr().getCurrentCountry().getSiteId() &&
				!isAgent &&
				!isJustRegistered &&
				!isSessionBridged,
		} as ICustomerStateStore;

		this.managerRepository.getStoreMgr().getStore().dispatch(receiveCustomer(customer));

		if (
			oldCustomer.isServer === customer.isServer &&
			(oldCustomer.customerId !== customer.customerId ||
				oldCustomer.usid !== customer.usid ||
				oldCustomer.siteId !== customer.siteId)
		) {
			this.invalidateContext();
		}

		if (
			oldCustomer.isServer === customer.isServer &&
			!isSessionBridged &&
			(oldCustomer.siteId !== customer.siteId ||
				oldCustomer.customerId !== customer.customerId ||
				oldCustomer.authType !== customer.authType)
		) {
			this.managerRepository.getCommerceApi().auth.clearSFCCSession();
		}

		if (oldCustomer.usid !== customer.usid || oldCustomer.authType !== customer.authType) {
			this.managerRepository.getEventsMgr().emit(EEventType.CUSTOMER_CHANGED, {
				customer: this.getCurrentCustomer(),
			});
		}
	}

	/**
	 * Get stored context from store
	 * @return {ICustomerQualifiers} contextData context data
	 */
	private getStoredContext(): ICustomerQualifiers {
		return selectContext(this.managerRepository.getStoreMgr().getStore().getState());
	}

	/**
	 * Store context data to store
	 * @param {ICustomerCustomQualifiers} contextData context data
	 */
	private storeContext(contextData: ICustomerQualifiers): void {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveContext(contextData));
		this.managerRepository.getProductsMgr().markProductsToBeRefreshed();
	}

	/**
	 * Store customer groups to store
	 * @param {Array<CustomerGroup>} customerGroups customer groups
	 */
	private storeCustomerGroups(customerGroups: Array<CustomerGroup>): void {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveCustomerGroups(customerGroups));
	}

	/**
	 * Retrieves customer from store
	 * @returns {ICustomerStateStore} stored customer
	 */
	private selectCustomerFromStore(): ICustomerStateStore {
		return selectCustomer(this.managerRepository.getStoreMgr().getStore().getState());
	}

	/**
	 * Set current customer
	 * @param {ICustomerAPI} customer - customer
	 * @param {boolean} isJustRegistered - set to true if customer comes from registration
	 * @param {boolean} isAgent - set to true if customer is an agent
	 * @param {boolean} isSessionBridged - set to true if the session is already bridged
	 */
	private setCurrentCustomer(
		customer: ICustomerAPI,
		isJustRegistered: boolean = false,
		isAgent: boolean = false,
		isSessionBridged: boolean = false,
	): any {
		return this.storeCustomer(
			{...customer, needsCommerceContext: customer.needsCommerceContext || !isServer},
			isJustRegistered, isAgent, isSessionBridged
		);
	}

	/**
	 * Set current customer groups
	 * @param {any} customerGroup customer groups
	 */
	private setCurrentCustomerGroups(customerGroup: any): void {
		this.storeCustomerGroups(customerGroup);
	}

	/**
	 * Set current customer A/B Tests
	 * @param {ABTestsResponse} abTests - A/B tests which current customer is participating
	 */
	private setCurrentABTestParticipations(abTests: ABTestsResponse): void {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveABTestParticipations(abTests));
	}

	/**
	 * Set current customer context data
	 * @param {any} contextData context data
	 */
	private setCurrentCustomerContext(contextData: any): void {
		const storedContext = this.getStoredContext();

		if (!storedContext || !isEqual(cleanObject(storedContext), contextData && cleanObject(contextData))) {
			this.storeContext(contextData);
		}
	}

	/**
	 * Invalidates current context
	 */
	public invalidateContext(): void {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveContext(null));
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveCustomerGroups([]));
	}

	/**
	 * Update customer newsletter flag
	 * @param {boolean} shouldSubscribe set flag
	 */
	updateCustomerNewsletter(shouldSubscribe: boolean): void {
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveNewsletterSubscribe(shouldSubscribe));
	}

	/**
	 * Logins the customer with the specified credentials.
	 * @param {ICustomerLoginCredentials | IIdentityProviderCredentials} credentials credentials
	 * @param {boolean} isJustRegistered set to true if customer comes from registration
	 * @param {Object} tokenData - tokenData
	 *
	 * @returns {Promise<Customer>} a promise resolving customer login
	 */
	async loginCustomer(
		credentials: ICustomerLoginCredentials | IIdentityProviderCredentials | null = null,
		isJustRegistered: boolean = false,
		tokenData = null,
	): Promise<Customer> {
		if (!this.loginPromise) {
			this.loginPromise = new Promise((resolve, reject) => {
				const loginCredentials = credentials as ICustomerLoginCredentials;

				if (loginCredentials?.customerData) {
					const {newsletterSource, ...registrationData} = cloneObject(loginCredentials.customerData);

					loginCredentials.customerData = this.prepareRegistrationData(
						registrationData,
						newsletterSource,
					) as unknown as ICustomerRegistrationData;
				}

				this.managerRepository
					.getCommerceApi()
					.auth.login(credentials as any, tokenData)
					.then((skeletonCustomer) => {
						if (skeletonCustomer.authType === ECustomerAuthType.GUEST) {
							this.setCurrentCustomer(
								skeletonCustomer,
								isJustRegistered,

								false,
								skeletonCustomer.isSessionBridged,
							);

							resolve(skeletonCustomer);
						} else {
							const isAgent = (credentials as any)?.type === ECustomerAuthType.AGENT;

							this.fetchCustomer(
								skeletonCustomer.customerId,
								isJustRegistered,
								isAgent,
								skeletonCustomer.isSessionBridged,
							)
								.then((customer) => {
									this.managerRepository.getEventsMgr().emit(EEventType.LOGS_IN, {
										customer: this.getCurrentCustomer(),
									});

									resolve(customer);
								})
								.catch((e) => {
									this.logoutCustomer();

									reject(e);
								});
						}
					})
					.catch((e) => {
						if (!credentials || e === 'PROFILE_NOT_FOUND' || e === 'PROFILE_INCOMPLETE') {
							/**
							 * In case it was a guest auth, it means there is an error in the session
							 * otherwise it means that the customer just used a wrong password.
							 */
							this.logoutCustomer();
						}

						reject(e);
					});
			});
		}

		try {
			const customer = await this.loginPromise;

			return customer;
			/* eslint no-useless-catch: 0 */
		} catch (e) {
			throw e;
		} finally {
			this.loginPromise = null;
		}
	}

	/**
	 * Log out current customer.
	 * and retrieve a guest access token
	 * @returns {Promise} a promise resolving customer logout
	 */
	async logoutCustomer(): Promise<any> {
		const customer = await this.managerRepository.getCommerceApi().auth.logout() as ICustomerAPI;

		this.managerRepository.getOrdersMgr().cleanupOrders();
		this.managerRepository.getStoreMgr().getStore().dispatch(resetCustomerProductLists());

		await this.setCurrentCustomer(customer);

		this.managerRepository.getStoreMgr().getStore().dispatch(resetBasket());
		this.managerRepository.getEventsMgr().emit(EEventType.LOGS_OUT, {
			customer: this.getCurrentCustomer(),
		});

		return Promise.resolve(customer);
	}

	/**
	 * Fetch information about current customer.
	 * @param {string} customerId - customer id
	 * @param {boolean} isJustRegistered - set to true if customer comes from registration
	 * @param {boolean} isAgent - set to true if customer is an agent
	 * @param {boolean} isSessionBridged - set to true if the session is already bridged
	 */
	async fetchCustomer(
		customerId: string | null = null,
		isJustRegistered: boolean = false,
		isAgent: boolean = false,
		isSessionBridged: boolean = false,
	): Promise<any> {
		const customer = await this.managerRepository.getCommerceApi().shopperCustomers.getCustomer({
			parameters: {customerId: customerId || this.getCurrentCustomer().customerId},
		}) as ICustomerAPI;

		if (customer.isError && !customer.enabled) {
			throw new Error(customer.detail);
		}

		if (isAgent) {
			this.managerRepository.getOrdersMgr().cleanupOrders();
		}

		await this.setCurrentCustomer(customer, isJustRegistered, isAgent, isSessionBridged);

		return customer;
	}

	/**
	 * Register a new customer account.
	 * @param {ICustomerRegistrationData} data - registration data object
	 * @param {String} newsletterSource - newsletter subscription source
	 * @param {String} registrationDevice - device type used for registration
	 * @param {String} subscriptionSource - subscription source
	 */
	async registerCustomer(
		data: ICustomerRegistrationData,
		newsletterSource: string | INewsletterSource,
		registrationDevice: string,
		subscriptionSource: string,
	): Promise<any> {
		if (typeof newsletterSource === 'object') {
			newsletterSource = newsletterSource.newsletterSourceDetails.pageType
				? newsletterSource.newsletterSourceDetails.pageType
				: newsletterSource.newsletterSource;
		}

		const body: ICustomerRegistrationPayload = {
			customer: this.prepareRegistrationData(
				data, newsletterSource, registrationDevice, subscriptionSource,
			) as ICustomerProfilePayload,
			password: data.password,
		};

		const response = await this.managerRepository
			.getCommerceApi()
			.shopperCustomersCustomProxy.registerCustomer({body});

		// Check for error json response
		if (response.detail && response.title && response.type) {
			if (
				response.title === 'Invalid Email' ||
				response.type === 'https://api.commercecloud.salesforce.com/documentation/error/v1/errors/invalid-email'
			) {
				throw new EmailValidationError(response.detail, response.title);
			}

			if (
				response.title === 'Login Already In Use' ||
				response.type ===
					'https://api.commercecloud.salesforce.com/documentation/error/v1/errors/login-already-in-use'
			) {
				throw new EmailAlreadyInUseError(response.detail, response.title);
			}

			throw new Error(response.detail);
		} else {
			const registrationEventData: {customerNo: string; newsletter: boolean; gender?: ECustomerGender} = {
				customerNo: response.customerNo,
				newsletter: !!response.c_newsletter,
			};

			if (response.gender) {
				registrationEventData.gender = response.gender;
			}

			this.managerRepository.getEventsMgr().emit(EEventType.CUSTOMER_REGISTRATION, registrationEventData);
		}

		// Send a new login request with the given credentials to ensure tokens are updated.
		return await this.loginCustomer({email: data.email, password: data.password}, true);
	}

	/**
	 * Update customer information.
	 * @param {ICustomerRegistrationData} data
	 * @param {Boolean} fetchUpdatedCustomer if true the full updated customer is fetched
	 * @param {boolean} isJustRegistered set to true if customer comes from registration
	 * @return Promise<any>
	 */
	async updateCustomer(
		data: ICustomerRegistrationData,
		fetchUpdatedCustomer: boolean = true,
		isJustRegistered: boolean = false,
	): Promise<any> {
		const currentLocale = this.managerRepository.getLocalesMgr().getCurrentLocale();
		const body: Partial<ICustomerProfilePayload> = {
			c_languageCode: currentLocale.getLanguageCode(),
			c_countryCode: currentLocale.getCountryCode(),
		};

		Object.keys(data).forEach((fieldKey) => {
			const fieldValue = data[fieldKey];

			switch (fieldKey) {
				case 'phoneCode':
					body.c_phoneCode = fieldValue;

					break;

				case 'citizenship':
					body.c_citizenship = fieldValue;

					break;

				case 'city':
					body.c_city = fieldValue;

					break;

				case 'country':
					body.c_countryCode = fieldValue;

					break;

				case 'phone':
					body.phoneHome = fieldValue;

					break;

				case 'firstName':
					body.firstName = fieldValue;

					break;

				case 'lastName':
					body.lastName = fieldValue;

					break;

				case 'gender':
					body.gender = typeof fieldValue === 'string' ? parseInt(fieldValue) : fieldValue;

					break;

				case 'salutation':
					body.salutation = fieldValue;
					body.gender = resolveGenderFromSalutation(undefined, fieldValue) ?? ECustomerGender.NO_PREFERENCE;

					break;
				case 'newsFor':
					body.c_newsFor = fieldValue;

					break;
				case 'currentPassword':
					body.c_currentPassword = fieldValue;

					break;
				case 'password':
					body.c_password = fieldValue;

					break;
				case 'source':
					body.c_source = fieldValue;

					break;
				case 'email':
					// NOTE/ISSUE
					// The sdk is allowing you to change your email to an already-existing email.
					// I would expect an error. We also want to keep the email and login the same
					// for the customer, but the sdk isn't changing the login when we submit an
					// updated email. This will lead to issues where you change your email but end
					// up not being able to login since 'login' will no longer match the email.
					body.email = fieldValue;
					body.login = fieldValue;

					break;
				case 'birthday':
					if (data.birthday) {
						body.birthday = formatDateToISOString(data.birthday);
					}

					break;
				case 'acceptsMarketing':
					body.c_newsletter = fieldValue;

					break;
				case 'authorizationDataCollection':
					body.c_authorizationDataCollection = fieldValue;

					break;
				case 'privacyAuthorization':
					body.c_privacyAuthorization = fieldValue;

					break;
				case 'newsletterSubscriptions':
					body.c_newsletterSubscriptions = fieldValue;

					break;
				case 'contactEmail':
					body.c_contactEmail = fieldValue;

					break;
				case 'contactSms':
					body.c_contactSms = fieldValue;

					break;
				case 'contactWhatsapp':
					body.c_contactWhatsapp = fieldValue;

					break;
				case 'contactPhone':
					body.c_contactPhone = fieldValue;

					break;
				case 'USConsent':
					body.c_USConsent = fieldValue;

					break;
				default:
					break;
			}
		});

		const response: any = await this.managerRepository.getCommerceApi().shopperCustomersCustomProxy.updateCustomer({
			body,
			parameters: {customerId: this.getCurrentCustomer().customerId},
		});

		// Check for error json response
		if (response.detail && response.title && response.type) {
			if (response.statusCode) {
				throw new PWAException(response.statusCode, response.statusMessage);
			} else {
				throw new Error(response.detail);
			}
		}

		if (fetchUpdatedCustomer) {
			// This previous request does return the updated customer profile, however it does
			// not include the 'entire' customer. It is missing address and payment methods.
			// We need to refetch the customer to make sure everything is up to date.
			return await this.fetchCustomer(null, isJustRegistered);
		} else {
			return response;
		}
	}

	/**
	 * Update customer password.
	 * @param {ICustomerUpdatePasswordData} data
	 */
	async updateCustomerPassword(data: ICustomerUpdatePasswordData) {
		const body: ICustomerUpdatePasswordData = {
			password: data.password,
			currentPassword: data.currentPassword,
		};

		// Note that we're using the raw response here. This request does not return
		// data when successful, but the sdk tries to parse the json (potential sdk bug).
		const rawResponse = await this.managerRepository.getCommerceApi().shopperCustomers.updateCustomerPassword(
			{
				body,
				parameters: {customerId: this.getCurrentCustomer().customerId},
			},
			true,
		);

		// Success has no json response to parse, but errors do, so handle that here.
		if (rawResponse.status >= 400) {
			const json = await rawResponse.json();

			// Check for error json response
			if (json.detail && json.title && json.type) {
				if (json.detail in errorsMapping) {
					throw new PWAException(errorsMapping[json.detail]);
				} else {
					throw new Error(json.detail);
				}
			}
		}
	}

	/**
	 * Add a new saved address.
	 * @param {ICustomerAddress} address
	 * @external Address
	 * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customeraddress
	 */
	async addCustomerSavedAddress(address: ICustomerAddress) {
		const customer = this.getCurrentCustomer();
		const addressesCount = customer?.addresses.length;

		// The `addressId` field is required to save the customer's address to their account.
		// Rather than make the customer provide a name/id, we can generate a unique id behind
		// the scenes instead. This is only useful if you are not displaying the addr name/id
		// in the UI, which we aren't.
		const body = {
			addressId: nanoid(),
			...address,
			preferred: addressesCount === 0 ? true : address.preferred,
		};

		try {
			await this.managerRepository.getCommerceApi().shopperCustomers.createCustomerAddress({
				body,
				parameters: {customerId: this.getCurrentCustomer().customerId},
			});
		} catch (e) {
			throw new Error('Unable to add address to customer');
		}

		// This endpoint does not return the updated customer object, so we manually fetch it
		await this.fetchCustomer();
	}

	/**
	 * Update a saved address.
	 * @param {ICustomerAddress} address
	 * @external Address
	 * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customeraddress
	 */
	async updateCustomerSavedAddress(address: ICustomerAddress) {
		const body = address;
		const addressName = address.addressName;
		delete address.addressName;

		try {
			await this.managerRepository.getCommerceApi().shopperCustomersCustomProxy.updateCustomerAddress({
				body: body as any,
				parameters: {
					customerId: this.getCurrentCustomer().customerId,
					addressName: encodeURIComponent(addressName),
				},
			});
		} catch (e) {
			throw new Error(`Unable to update client address with this addressId: ${address.addressId}`);
		}

		// This endpoint does not return the updated customer object, so we manually fetch it
		await this.fetchCustomer();
	}

	/**
	 * Add a new saved payment instrument.
	 * @param {ICustomerPaymentInstrument} paymentInstrument
	 * @external PaymentInstrument
	 * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shoppercustomers.html#customerpaymentinstrumentrequest
	 */
	async addCustomerSavedPaymentInstrument(paymentInstrument: ICustomerPaymentInstrument) {
		const body = {
			bankRoutingNumber: '',
			giftCertificateCode: '',
			...paymentInstrument,
			paymentCard: {
				...paymentInstrument.paymentCard,
			},
		};
		await this.managerRepository.getCommerceApi().shopperCustomers.createCustomerPaymentInstrument({
			body: body as Record<string, unknown>,
			parameters: {customerId: this.getCurrentCustomer().customerId},
		});

		// This endpoint does not return the updated customer object, so we manually fetch it
		await this.fetchCustomer();
	}

	/**
	 * Remove a saved payment instrument.
	 * @param {string} paymentInstrumentId - The id of the payment payment instrument.
	 */
	async removeCustomerSavedPaymentInstrument(paymentInstrumentId: string) {
		// This SDK method must be called with `true` as second argument to avoid an error in
		// the sdk where it tries parsing json from an empty http response.
		await this.managerRepository.getCommerceApi().shopperCustomers.deleteCustomerPaymentInstrument(
			{
				parameters: {customerId: this.getCurrentCustomer().customerId, paymentInstrumentId},
			},
			true,
		);

		// This endpoint does not return the updated customer object, so we manually fetch it
		await this.fetchCustomer();
	}

	/**
	 * Remove a saved address.
	 * @param {string} addressId - The id of the saved address.
	 */
	async removeCustomerSavedAddress(addressId: string) {
		// This SDK method must be called with `true` as second argument to avoid an error in
		// the sdk where it tries parsing json from an empty http response.
		await this.managerRepository.getCommerceApi().shopperCustomers.removeCustomerAddress(
			{
				parameters: {
					customerId: this.getCurrentCustomer().customerId,
					addressName: encodeURIComponent(addressId),
				},
			},
			true,
		);

		// This endpoint does not return the updated customer object, so we manually fetch it
		await this.fetchCustomer();
	}

	/**
	 * Get URL for social Login flow using specified identity provider
	 *
	 * @param {string} identityProvider the identity provider to be used (e.g. 'Google', 'Facebook', etc...)
	 * @returns {Promise} a promise resolving to the url for social login
	 */
	async getSocialLoginURL(identityProvider: string): Promise<any> {
		return await this.managerRepository.getCommerceApi().auth.getSocialLoginURL(identityProvider);
	}

	/**
	 * Sends email preferences to Emarsys
	 * @param {string} email - customer email
	 * @param {string[]} preferences - customer email preferences
	 * @returns {Promise} a promise resolving to the response from SFCC
	 */
	async addNewsletterPreferences(email: string, preferences: string[]): Promise<any> {
		return this.managerRepository.getCommerceApi().customShopperNewsletter.addNewsletterPreferences({
			pathParameters: {
				c_email: email,
			},
			body: {
				preferences,
			},
		});
	}

	//@TODO: Move parameters to an object
	/**
	 * Subscribes the user to newsletter
	 * @param {string} email - customer email
	 * @param {string} gender - customer gender
	 * @param {EEventSource} eventSource - eventSource
	 * @param {string} source - source
	 * @param {string[]} subscriptions - list of subscriptions for the newsletter
	 * @param authorizationDataCollection
	 * @param acceptsMarketing
	 * @param {string} newsFor
	 * @param {string} firstName - firstName
	 * @param {string} lastName - lastName
	 * @param {string} birthday - birthday
	 * @param {string} salutation - salutation
	 * @param {string} phoneCode - phoneCode
	 * @param {string} phone - phone
	 * @param consentFieldName - name of the body field used to send data-collection consent, for callers whose
	 * site requires a field name other than the default `authorizationDataCollection`
	 * @returns {Promise} a promise resolving to the response from SFCC
	 */
	async subscribeToNewsletter(
		email: string,
		gender?: string,
		eventSource: EEventSource = EEventSource.FOOTER,
		source?: string | INewsletterSource,
		subscriptions?: string[],
		authorizationDataCollection = null,
		acceptsMarketing = false,
		newsFor: string = '',
		firstName?: string,
		lastName?: string,
		birthday?: string,
		salutation?: string,
		phoneCode?: string,
		phone?: string,
		brandPreference?: string[],
		contactEmail?: boolean,
		contactSms?: boolean,
		contactWhatsapp?: boolean,
		contactPhone?: boolean,
		consentFieldName: 'authorizationDataCollection' | 'USConsent' = 'authorizationDataCollection',
	): Promise<any> {
		const currentCustomer = this.getCurrentCustomer();
		const isLoggedIn = currentCustomer.isRegistered;
		const currentLocale = this.managerRepository.getLocalesMgr().getCurrentLocale();

		this.managerRepository.getEventsMgr().emit(EEventType.NEWSLETTER_SUBSCRIPTION, {
			email,
			eventSource,
			isLoggedIn,
		});

		if (typeof source === 'object') {
			source = source.newsletterSourceDetails?.pageType || source.newsletterSource;
		}

		const resolvedGender = resolveGenderFromSalutation(gender, salutation);
		const dataCollectionConsent = authorizationDataCollection ?? true;

		return this.managerRepository.getCommerceApi().customShopperNewsletter.subscribeToNewsletter(
			{
				pathParameters: {
					c_email: email,
				},
				body: {
					gender: resolvedGender !== undefined ? String(resolvedGender) : '',
					firstName,
					lastName,
					birthday,
					newsFor,
					countryCode: currentLocale.getCountryCode(),
					languageCode: currentLocale.getLanguageCode(),
					source: source ?? '',
					subscriptions: subscriptions ?? [],
					[consentFieldName]: dataCollectionConsent,
					acceptsMarketing: acceptsMarketing ?? true,
					brandPreference,
					salutation: salutation ?? '',
					phoneCode: phoneCode ?? '',
					phone: phone ?? '',
					contactEmail,
					contactSms,
					contactWhatsapp,
					contactPhone,
				}
			},
			true,
		);
	}

	/**
	 * Unsubscribes the user to newsletter
	 * @param {string} email - customer email
	 * @param {string} unsubscribeReason - unsubscribe reason
	 * @returns {Promise} a promise resolving to the response from SFCC
	 */
	async unsubscribeToNewsletter(email: string, unsubscribeReason: string): Promise<any> {
		return this.managerRepository.getCommerceApi().customShopperNewsletter.unsubscribeFromNewsletter(
			{
				pathParameters: {
					c_email: email,
				},
				parameters: {
					c_unsubscribeReason: unsubscribeReason ?? '',
				},
			},
			true,
		);
	}

	/**
	 * Unsubscribes the customer from the newsletter using a uid token
	 * (DELETE custom API profiles/uid/{c_uid}/subscription).
	 * @param {string} uid - opaque unsubscribe uid from the marketing link
	 * @returns {Promise<IUnsubscribeFromNewsletterByUidResponse>} SFCC custom newsletter unsubscribe-by-uid response
	 */
	async unsubscribeFromNewsletterByUid(uid: string): Promise<IUnsubscribeFromNewsletterByUidResponse> {
		return this.managerRepository.getCommerceApi().customShopperNewsletter.unsubscribeFromNewsletterByUid(
			{
				pathParameters: {
					c_uid: uid,
				},
			},
			true,
		);
	}

	/**
	 * Updates newsletter profile by email
	 * @param {string} email - customer email
	 * @param {INewsletterPreferences} preferences - newsletter preference
	 * @param {boolean} shouldUpdateStore - updates the store with the newsletter preferences
	 * @returns {Promise} a promise resolving to existing status
	 */
	async updateNewsletterProfile(email: string, preferences: INewsletterPreferences, shouldUpdateStore = false) {
		const currentLocale = this.managerRepository.getLocalesMgr().getCurrentLocale();

		if (shouldUpdateStore) {
			const store = this.managerRepository.getStoreMgr().getStore();

			store.dispatch(receiveNewsletterCustomization(preferences));
		}

		const resolvedGender = resolveGenderFromSalutation(preferences.gender, preferences.salutation);

		const body: IUpdateNewsletterProfileOptions['body'] = {
			gender: resolvedGender !== undefined ? String(resolvedGender) : undefined,
			salutation: preferences.salutation,
			countryCode: currentLocale.getCountryCode(),
			languageCode: currentLocale.getLanguageCode(),
			preferredNewsletterContent: preferences.preferredNewsletterContent,
			preferredCategories: preferences.preferredCategories,
			preferredBrands: preferences.preferredBrands,
			followedBrands: preferences.followedBrands,
			isSubscribed: preferences.isSubscribed,
			privacyAuthorization: preferences.privacyAuthorization,
			authorizationDataCollection: preferences.authorizationDataCollection,
			contactEmail: preferences.contactEmail,
			contactSms: preferences.contactSms,
			contactWhatsapp: preferences.contactWhatsapp,
			contactPhone: preferences.contactPhone,
			brandPreference: preferences.brandPreference
		};

		if (preferences.newsFor) {
			body.newsFor = String(preferences.newsFor);
		}

		if (typeof preferences.USConsent === 'boolean') {
			body.USConsent = preferences.USConsent;
		}

		return this.managerRepository.getCommerceApi().customShopperNewsletter.updateNewsletterProfile(
			{
				pathParameters: {
					c_email: email
				},
				body
			},
			true
		);
	}

	/**
	 * Create newsletter profile by email
	 * @param {string} email - customer email
	 * @returns {Promise} a promise resolving to existing status
	 */
	async createProfile(email: string, countryCode: string, languageCode: string): Promise<any> {
		return this.managerRepository.getCommerceApi().customShopperNewsletter.createNewsletterProfile({
			pathParameters: {
				c_email: email,
			},
			body: {
				countryCode: countryCode,
				languageCode: languageCode,
			},
		});
	}

	/**
	 * Returns newsletter profile by email
	 * @param {string} email - customer email
	 * @param {boolean} shouldUpdateStore - updates the store with the newsletter preferences
	 * @returns {Promise<INewsletterProfile>} a promise resolving to existing status
	 */
	async getNewsletterProfile(email: string, shouldUpdateStore = false): Promise<INewsletterProfile> {
		const body: ISearchNewsletterProfileOptions['body'] = {email};

		return this.managerRepository
			.getCommerceApi()
			.customShopperNewsletter.searchNewsletterProfile(
				{
					body,
				},
				true,
			)
			.then((res) => {
				const profile: INewsletterProfile = {
					...res.newsletterProfile,
					gender: (res.newsletterProfile.gender ? res.newsletterProfile.gender : undefined) as ECustomerGender | undefined,
					newsFor: res.newsletterProfile.newsFor ?? '',
					brandPreference: res.newsletterProfile.brandPreference || [],
					preferredNewsletterContent: res.newsletterProfile.preferredNewsletterContent || [],
					preferredCategories: res.newsletterProfile.preferredCategories || [],
					preferredBrands: res.newsletterProfile.preferredBrands || [],
				};

				if (shouldUpdateStore) {
					const store = this.managerRepository.getStoreMgr().getStore();
					store.dispatch(receiveNewsletterCustomization(profile));
				}

				return profile;
			});
	}

	/**
	 * Joins user to loyalty programs
	 * @returns {Promise} a promise resolving to the response from SFCC
	 */
	async joinToLoyalty(): Promise<any> {
		return this.managerRepository.getCommerceApi().customShopperLoyalty.joinToLoyalty({});
	}

	/**
	 * Checks if customer exist by email
	 * @param {string} email - customer email
	 * @returns {Promise} - a promise resolving to existing status
	 */
	async getCustomerExistingInfo(email: string): Promise<any> {
		return this.managerRepository
			.getSfccAPI()
			.checkIfExistCustomer(email)
			.then((res) => {
				return {
					existingStatus: res.existingStatus,
					shouldResetPassword: res.shouldResetPassword,
				};
			});
	}

	/**
	 * Resets password for customer
	 * @param {string} email - customer email
	 * @returns {Promise} - a promise resolving if successful was resetting password
	 */
	async resetCustomerPassword(email: string): Promise<any> {
		return this.managerRepository.getSfccAPI().resetPassword(email);
	}

	/**
	 * Sets new password by token
	 * @param {string} token - reset password token
	 * @param {string} password - new password
	 * @returns {Promise} - a promise resolving setting of new password
	 */
	async setCustomerNewPasswordByToken(token: string, password: string): Promise<any> {
		return this.managerRepository.getSfccAPI().setNewPasswordByToken({
			token: token,
			password: password,
		});
	}

	/**
	 * Check reset password token
	 * @param {string} token - reset password token
	 * @returns {Promise} - a promise resolving token validity
	 */
	async checkTokenValidity(token: string): Promise<any> {
		return this.managerRepository.getSfccAPI().checkTokenValidity({
			token: token,
		});
	}

	/**
	 * Complete the OAuth flow by retrieving an access token on redirect from idp
	 *
	 * @param {string} usid the usid generated by SLAS
	 * @param {string} code the authentication code generated by the idp
	 * @returns {Promise} a promise resolving login token
	 */
	async getSocialLoginToken(usid: string, code: string): Promise<any> {
		return await this.managerRepository.getCommerceApi().auth.getSocialLoginToken(usid, code);
	}

	/**
	 * Complete the OAuth flow by retrieving an access token on redirect from direct call to provider
	 *
	 * @param {string} providerId provider ID that process auth
	 * @param {string} code the authentication code generated by the idp
	 * @param {string} redirectUrl redirect URL provided to service
	 * @returns {Promise} a promise resolving login token
	 */
	async identityProviderLogin(providerId: string, code: string, redirectUrl: string): Promise<any> {
		return this.managerRepository.getSfccAPI().identityProviderLogin({
			providerId: providerId,
			providerData: {
				code: code,
				redirectUrl: redirectUrl,
			},
		});
	}

	/**
	 * Try to get information about customer from selected provider
	 * and if customer is already registered then auto-login
	 * @param {string} credentials - provider credentials
	 */
	async authenticateExternalCustomer(credentials: object): Promise<any> {
		const customer = await this.managerRepository.getCommerceApi().auth.authenticateProviderLogin(credentials);

		if (!customer) {
			return null;
		}

		if (customer.externalProfile) {
			return customer;
		}

		await this.setCurrentCustomer(customer, false);

		return await this.fetchCustomer(customer.customerId).then((customer) => {
			this.managerRepository.getEventsMgr().emit(EEventType.LOGS_IN, {
				customer: this.getCurrentCustomer(),
			});

			return customer;
		});
	}

	/**
	 * Try to register external user
	 * @param {object} externalProfile - external profile object
	 */
	async registerExternalCustomer(externalProfile: object): Promise<any> {
		const customer = await this.managerRepository.getCommerceApi().auth.registerExternalProfile(externalProfile);

		if (!customer) {
			return null;
		}

		await this.setCurrentCustomer(customer, false);

		return await this.fetchCustomer(customer.customerId).then((customer) => {
			this.managerRepository.getEventsMgr().emit(EEventType.LOGS_IN, {
				customer: this.getCurrentCustomer(),
			});

			return customer;
		});
	}

	/**
	 * Delete Api Context
	 */
	async deleteApiContext() {
		return await this.managerRepository.getCommerceApi().shopperContexts.deleteShopperContext({});
	}

	/**
	 * Create a cookie context for the current customer
	 * This function creates a cookie context based on the current customer's registration status.
	 * If the customer is registered, the cookie expiration after 7 days.
	 * If the customer is a guest, the cookie expiration after 1 days.
	 */
	createContextCookie(): void {
		const currentCustomer: Customer = this.getCurrentCustomer();
		const usidCookies: Array<Cookie> = CookiesMgr.getCookiesByPattern('^usid_.*');
		const contextCookies: Array<Cookie> = CookiesMgr.getCookiesByPattern('^context_.*');
		const validUsids: Set<string> = new Set(usidCookies.map((cookie) => cookie.getValue()));

		contextCookies.forEach((cookie: Cookie): void => {
			const usid: string = cookie.getKey().replace('context_', '');

			if (!validUsids.has(usid)) {
				CookiesMgr.removeCookie(cookie.getKey());
			}
		});

		const cookieAttributes: Record<string, string | number> = {
			expires: currentCustomer.isRegistered
				? REFRESH_CONTEXT_USER_DAYS_TIME.REGISTERED
				: REFRESH_CONTEXT_USER_DAYS_TIME.GUEST,
		};
		const cookie: Cookie = new Cookie(`context_${currentCustomer.usid}`, 1, cookieAttributes);

		CookiesMgr.setCookie(cookie);
	}

	/**
	 * Check if the context cookie is valid.
	 * This function checks if the context cookie is valid by comparing it with the current customer's ID.
	 * If the cookie context is missing or does not match the current customer's ID, it triggers the creation or update of the customer context.
	 */
	async validateCustomerContext() {
		if (isServer || !this.getCurrentCustomer().isReady() || !this.isContextAvailable()) {
			return;
		}

		if (this.contextUpdatingPromises.size > 0) {
			await Promise.all(Array.from(this.contextUpdatingPromises.values()));
		}

		if (this.contextValidatingPromise) {
			await this.contextValidatingPromise;

			return;
		}

		const currentCustomer = this.managerRepository.getCustomerMgr().getCurrentCustomer();
		const cookieContext = CookiesMgr.getCookie(`context_${currentCustomer.usid}`).getValue();

		if (!cookieContext) {
			this.contextValidatingPromise = this.refreshCustomerContext(true);
			await this.contextValidatingPromise;
			this.contextValidatingPromise = null;
		}
	}

	/**
	 * Get or create customer context
	 * @param {object} payload payload
	 * @returns {Promise} a promise resolving customer context
	 */
	async getOrCreateApiContext(payload: ICustomerQualifiers = {customQualifiers: {}} as ICustomerQualifiers): Promise<any> {
		let resp: any = await this.managerRepository.getCommerceApi().shopperContexts.getShopperContext({});

		if (
			resp.error_code === 'NOT_FOUND' ||
			resp.type ===
				'https://api.commercecloud.salesforce.com/documentation/error/v1/errors/shopper-context-not-found' ||
			resp.title === 'ShopperContextNotFoundException' ||
			resp.title === 'Shopper Context Not Found' ||
			!!(resp.detail && resp.detail.indexOf('not found') !== -1)
		) {
			await this.managerRepository.getCommerceApi().shopperContexts.createShopperContext({body: payload});
			resp = payload;

			if (!isServer) {
				this.createContextCookie();
			}
		} else if (!isEqual(cleanObject(payload), cleanObject(resp))) {
			resp = await this.managerRepository.getCommerceApi().shopperContexts.updateShopperContext({body: payload});
		}

		return resp;
	}

	/**
	 * Update SFCC Commerce customer context
	 * @param {ICustomerQualifiers} payload payload
	 * @returns {Promise<any>}
	 */
	async updateSFCCAPIContext(payload: ICustomerQualifiers): Promise<any> {
		const cachedContext = this.managerRepository.getStaticCacheMgr().setContext(payload);

		if (cachedContext) {
			this.setCurrentCustomerGroups((cachedContext as {customerGroups: CustomerGroup[]}).customerGroups);
		} else {
			return this.managerRepository
				.getSfccAPI()
				.setContext(payload)
				.then((contextResponse) => {
					this.setCurrentCustomerGroups(contextResponse.customerGroups);

					this.setCurrentABTestParticipations(contextResponse.ab);
				});
			/*
				Customer groups in SCAPI seems to not reflect the session.custom attribute
				nor the customQualifiers set via ContextAPI.
				We await for a feedback by Salesforce to re-enable it.

				const setContextResponse = await this.managerRepository
					.getCommerceApi()
					.customShopperContext
					.setCustomerContext({
						body: {
							customQualifiers: payload.customQualifiers
						}
					});

				return  this.setCurrentCustomerGroups(setContextResponse.customerGroups);
			 */
		}
	}

	/**
	 * Init shopper contexts in SCAPI and CAPI
	 * If any context updates are in progress, it awaits them to be complete.
	 * @param {ICustomerQualifiers} frozenPayload Custom session attributes that should be set
	 * @returns {Promise} a promise resolving customer context initialization
	 */
	async initCustomerContext(frozenPayload: ICustomerQualifiers = {customQualifiers: {}} as ICustomerQualifiers): Promise<any> {
		let updatingPromiseResolve;
		const currentCustomer = this.getCurrentCustomer();
		const updatingPromise = new Promise<void>((resolve) => (updatingPromiseResolve = resolve));
		const promiseKey = v4();

		this.contextUpdatingPromises.set(promiseKey, updatingPromise);

		const awaitPromises = Array.from(this.contextUpdatingPromises.keys()).reduce((acc, key) => {
			if (key !== promiseKey) {
				return [...acc, this.contextUpdatingPromises.get(key)];
			} else {
				return acc;
			}
		}, []);

		await Promise.all(awaitPromises);

		const resolveAll = () => {
			updatingPromiseResolve();
			this.contextUpdatingPromises.delete(promiseKey);
		};

		if (currentCustomer.needsCommerceContext) {
			try {
				const awaitContextPromises = [this.getOrCreateApiContext(frozenPayload)];

				requestIdleCallback(() => this.updateSFCCAPIContext(frozenPayload));

				const [apiShopperContext] = await Promise.all(awaitContextPromises);

				// The context must be set since it was reset at the start
				this.setCurrentCustomerContext(frozenPayload);

				if (apiShopperContext.error_code) {
					await this.logoutCustomer();
				}
			} catch (error) {
				this.managerRepository
					.getLogMgr()
					.getLogProvider()
					?.error(
						JSON.stringify({
							error: 'SCAPI context error',
							exception: error.message,
							token: this.managerRepository.getCommerceApi().auth.authToken,
							usid: this.managerRepository.getCommerceApi().auth.usid,
							customer: {
								id: currentCustomer.customerId,
								isRegistered: currentCustomer.isRegistered,
							},
							userAgent: window.navigator.userAgent,
						}),
						LogSource.SCAPI as unknown as LogSource.COMPONENT,
						{},
					);

				this.setCurrentCustomerContext(frozenPayload);
				this.contextErrorCounter++;

				// The context must be set since it was reset at the start
				if (this.contextErrorCounter <= CONTEXT_ERROR_THRESHOLD) {
					await this.logoutCustomer();
				}
			}

			resolveAll();
		} else {
			// The context must be set since it was reset at the start
			this.setCurrentCustomerContext(frozenPayload);
			await this.updateSFCCAPIContext(frozenPayload);

			resolveAll();
		}
	}

	/**
	 * Refresh CAPI context and sync with SFCC API context
	 * @returns {Promise} a promise resolving customer context refresh
	 */
	async refreshCustomerContext(deleteBefore = false): Promise<any> {
		if (deleteBefore) {
			await this.deleteApiContext();
		}

		return await this.initCustomerContext(this.getStoredContext());
	}

	/**
	 * Restores the context to the initial app state
	 * @returns {void}
	 */
	async resetStore(): Promise<void> {
		this.managerRepository.getStoreMgr().getStore().dispatch(resetToInitialState() as any);
	}

	/**
	 * Update context attributes in SCAPI and CAPI
	 * @param {ICustomerCustomQualifiers} payload Custom session attributes that should be updated
	 * @returns {Promise} a promise resolving customer context qualifiers update
	 */
	async updateCustomerContextQualifiers(payload: ICustomerCustomQualifiers): Promise<any> {
		const customer = this.getCurrentCustomer();
		const ctx = {
			customQualifiers: payload,
			assignmentQualifiers: {
				platform: this.CONTEXT_PLATFORM_WEB,
				shippingCountry: payload.shippingCountry,
			},
		} as ICustomerQualifiers;

		// TODO: sourceCode should be passed into ctx param.
		if (!isServer) {
			const sourceCodeURLValue = new URLSearchParams(location.search).get('src');

			if (sourceCodeURLValue && sourceCodeURLValue.length) {
				ctx.sourceCode = sourceCodeURLValue;
			}
		}

		if (!customer.context) {
			await this.initCustomerContext(ctx);
		} else if (!isServer) {
			//TODO: understand if this block can be removed
			const updatedContext = merge({}, customer.context, ctx);
			const apiShopperContext = await this.managerRepository
				.getCommerceApi()
				.shopperContexts.updateShopperContext({body: updatedContext});
			this.updateSFCCAPIContext(updatedContext);

			if (!apiShopperContext.error_code) {
				this.setCurrentCustomerContext(apiShopperContext);
				this.managerRepository.getProductsMgr().markProductsToBeRefreshed();
			}
		}

		return Promise.resolve();
	}

	/**
	 * Update context effective Date in SCAPI and CAPI
	 * @param {Date} date Qualifier to set the effective date time for the context to apply. For example -> Shop the Future use cases.
	 * @returns {Promise} a promise resolving customer context effective date update
	 */
	async updateCustomerContextEffectiveDate(date: Date = new Date()): Promise<any> {
		const customer = this.getCurrentCustomer();
		const updatedContext = merge({}, customer?.context);
		updatedContext.effectiveDateTime = date.toISOString().split('.')[0] + 'Z';

		return this.managerRepository
			.getCommerceApi()
			.shopperContexts.updateShopperContext({body: updatedContext})
			.then((apiRes: any) => {
				this.setCurrentCustomerContext(apiRes);
				this.managerRepository.getProductsMgr().markProductsToBeRefreshed();
			});
	}

	/**
	 * Synchronizes the session with SFCC one
	 * @param {String} basketId - basket id
	 * @param {Boolean} force - if true session is always synchronized
	 */
	async syncSession(basketId: string = null, force: boolean = false): Promise<any> {
		const res = await this.managerRepository.getSfccAPI().syncSession(basketId, force);
		let returnObject;

		if (!res.success) {
			const currentCustomer = this.getCurrentCustomer();

			this.managerRepository
				.getLogMgr()
				.getLogProvider()
				?.error(
					JSON.stringify({
						error: 'Session sync error',
						response: res,
						token: this.managerRepository.getCommerceApi().auth.authToken,
						usid: this.managerRepository.getCommerceApi().auth.usid,
						customer: {
							id: currentCustomer.customerId,
							isRegistered: currentCustomer.isRegistered,
						},
						userAgent: window.navigator.userAgent,
					}),
					LogSource.SESSION as unknown as LogSource.COMPONENT,
					{},
				);

			returnObject = await this.logoutCustomer();
		} else if (res.shouldRefresh) {
			this.managerRepository.getCommerceApi().auth.deleteAuthToken();

			returnObject = await this.loginCustomer();

			this.managerRepository.getStoreMgr().getStore().dispatch(resetBasket());
		} else {
			returnObject = this.managerRepository.getCommerceApi().auth._reuseCurrentLogin();
		}

		return returnObject;
	}

	/**
	 * Checks if context is available
	 * @returns {boolean} true if context is available
	 */
	isContextAvailable(): boolean {
		return isServer || !!selectContext(this.managerRepository.getStoreMgr().getStore().getState());
	}

	/**
	 * Returns the current customer context
	 * @returns {ICustomerQualifiers} current customer context
	 */
	getCustomerContext(): ICustomerQualifiers {
		return this.getStoredContext();
	}
}

export default CustomerMgr;
